import { useEffect, useState } from 'react';
import { apiBaseUrl, getHealth, getSettings } from './api/client.js';
import RolesAndQuestions from './components/RolesAndQuestions.jsx';
import WeeklyWizard from './components/WeeklyWizard.jsx';
import QuarterlyWizard from './components/QuarterlyWizard.jsx';
import CalendarView from './components/CalendarView.jsx';
import PerfectYearAgenda from './components/PerfectYearAgenda.jsx';

const TABS = [
  { id: 'wizard', label: 'Weekly wizard' },
  { id: 'perfect_year', label: 'Perfect year' },
  { id: 'calendar', label: 'Calendar' },
  { id: 'quarterly', label: 'Quarterly review' },
  { id: 'settings', label: 'Role settings' },
];

export default function App() {
  const [health, setHealth] = useState({ status: 'checking', data: null });
  const [tab, setTab] = useState('wizard');
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    let cancelled = false;
    getHealth()
      .then((data) => {
        if (cancelled) return;
        setHealth({ status: 'ok', data });
      })
      .catch((err) => {
        if (cancelled) return;
        setHealth({ status: 'error', data: { error: String(err.message || err) } });
      });
    getSettings()
      .then((s) => {
        if (!cancelled) setSettings(s);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <div>
            <h1 className="text-xl font-semibold text-slate-900">LT-POS</h1>
            <p className="text-xs text-slate-500">
              Personal operating system — Weekly Meeting Wizard &amp; Perfect Year Calendar
            </p>
          </div>
          <div className="flex items-center gap-2">
            <NextMeetingCountdown settings={settings} />
            <HealthIndicator health={health} />
          </div>
        </div>
        <nav className="max-w-5xl mx-auto px-6 flex gap-1 -mb-px">
          {TABS.map((t) => {
            const active = t.id === tab;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                className={`px-3 py-2 text-sm border-b-2 -mb-px transition ${
                  active
                    ? 'border-slate-900 text-slate-900 font-medium'
                    : 'border-transparent text-slate-500 hover:text-slate-800'
                }`}
              >
                {t.label}
              </button>
            );
          })}
        </nav>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        {tab === 'wizard' && <WeeklyWizard />}
        {tab === 'perfect_year' && <PerfectYearAgenda />}
        {tab === 'calendar' && <CalendarView />}
        {tab === 'quarterly' && <QuarterlyWizard />}
        {tab === 'settings' && <RolesAndQuestions />}
      </main>
    </div>
  );
}

function NextMeetingCountdown({ settings }) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(id);
  }, []);

  if (!settings) return null;
  const dow = parseInt(settings.meeting_day_of_week ?? '0', 10);
  const time = settings.meeting_time || '11:00';
  const [hh, mm] = time.split(':').map((n) => parseInt(n, 10) || 0);

  const next = new Date(now);
  next.setHours(hh, mm, 0, 0);
  const dayDiff = (dow - next.getDay() + 7) % 7;
  next.setDate(next.getDate() + dayDiff);
  if (next.getTime() <= now.getTime()) next.setDate(next.getDate() + 7);

  const diffMs = next.getTime() - now.getTime();
  const days = Math.floor(diffMs / 86_400_000);
  const hours = Math.floor((diffMs % 86_400_000) / 3_600_000);
  const mins = Math.floor((diffMs % 3_600_000) / 60_000);
  const dayLabel = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][dow] || '—';
  const timeLabel = `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
  const within24h = days === 0;

  return (
    <div
      className={`hidden sm:inline-flex items-center gap-2 text-xs font-medium px-2.5 py-1 rounded-full border ${
        within24h
          ? 'bg-amber-50 text-amber-800 border-amber-200'
          : 'bg-slate-50 text-slate-600 border-slate-200'
      }`}
      title={`Next weekly meeting: ${next.toLocaleString()}`}
    >
      <span>📅 {dayLabel} {timeLabel}</span>
      <span className="text-slate-400">·</span>
      <span>
        {days > 0 ? `${days}d ` : ''}
        {hours}h {mins}m
      </span>
    </div>
  );
}

function HealthIndicator({ health }) {
  const tone =
    health.status === 'ok'
      ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
      : health.status === 'error'
      ? 'bg-rose-50 text-rose-700 border-rose-200'
      : 'bg-slate-50 text-slate-500 border-slate-200';
  const dot =
    health.status === 'ok'
      ? 'bg-emerald-500'
      : health.status === 'error'
      ? 'bg-rose-500'
      : 'bg-slate-400';
  const label =
    health.status === 'ok'
      ? `Worker ${health.data?.phase || 'live'}`
      : health.status === 'error'
      ? 'Worker unreachable'
      : 'Checking worker…';

  return (
    <div
      className={`inline-flex items-center gap-2 text-xs font-medium px-2.5 py-1 rounded-full border ${tone}`}
      title={apiBaseUrl || 'VITE_WORKER_URL not set'}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />
      {label}
    </div>
  );
}
